/**
 * passwordFormViewModel — la validation des deux écrans de mot de passe d'avant connexion, et le mot
 * de félicitation qui suit un envoi réussi.
 *
 * Les règles sont celles des schémas yup du legacy (`src/auth/ForgotPassword.js`,
 * `src/auth/ResetPasswordScreen.js`) : une adresse `email().required()`, un mot de passe requis de
 * 8 caractères au moins, une confirmation requise et égale. Les mots changent à un endroit : une
 * adresse vide ou invalide dit « Saisissez une adresse e-mail valide. », comme la connexion
 * (`src/auth/LoginScreen.js`), là où le legacy disait « Champ obligatoire » ou « Email invalide ».
 *
 * Pur : les crochets d'écran l'appellent à chaque rendu et ne montrent ses messages qu'après un
 * premier envoi (le `reValidateMode: onChange` de react-hook-form, que le legacy avait par défaut).
 */
import yup from "../../../../../data/utility/validation"

export const EMAIL_INVALID = "Saisissez une adresse e-mail valide."
/** Le message de `yup.setLocale` (`data/utility/validation`), que le legacy montrait. */
export const FIELD_REQUIRED = "Champ obligatoire"
export const PASSWORD_TOO_SHORT = "Le mot de passe doit contenir 8 caractères minimum"
export const PASSWORD_MISMATCH = "Le mot de passe de confirmation ne correspond pas"
const PASSWORD_MIN_LENGTH = 8

/** `congratsTypes.GENERIC` de l'écran de félicitations legacy (`common/screens/CongratsScreen`). */
const GENERIC_CONGRATS = "GENERIC"

export const FORGOT_PASSWORD_SENT = {
    title: "Effectué",
    text: "Nous vous avons envoyé par e-mail le lien de réinitialisation du mot de passe.",
}

export const PASSWORD_RESET_DONE = {
    title: "Enregistré",
    text: "Votre mot de passe a bien été mis à jour.",
}

/** Ce que `putCongrats` range pour l'écran `congrats`, qui reste legacy. */
export function genericCongrats(message: { title: string; text: string }) {
    return [{ congratsType: GENERIC_CONGRATS, congratsData: { title: message.title, text: message.text } }]
}

const EMAIL_RULE = yup.string().email().required()

/** La règle d'adresse des trois écrans d'avant connexion — connexion comprise. */
export function isValidEmail(email: string): boolean {
    return EMAIL_RULE.isValidSync(email)
}

export type ResetPasswordValues = {
    email: string
    password: string
    passwordConfirmation: string
}

export type ResetPasswordErrors = {
    emailError?: string
    passwordError?: string
    passwordConfirmationError?: string
}

export function forgotPasswordEmailError(email: string): string | undefined {
    return isValidEmail(email) ? undefined : EMAIL_INVALID
}

export function resetPasswordErrors(values: ResetPasswordValues): ResetPasswordErrors {
    const errors: ResetPasswordErrors = {}
    if (!isValidEmail(values.email)) errors.emailError = EMAIL_INVALID
    if (!values.password) errors.passwordError = FIELD_REQUIRED
    else if (values.password.length < PASSWORD_MIN_LENGTH) errors.passwordError = PASSWORD_TOO_SHORT
    if (!values.passwordConfirmation) errors.passwordConfirmationError = FIELD_REQUIRED
    else if (values.passwordConfirmation !== values.password) errors.passwordConfirmationError = PASSWORD_MISMATCH
    return errors
}

export function hasErrors(errors: ResetPasswordErrors): boolean {
    return Object.values(errors).some((message) => message != null)
}
